import type { FunctionalComponent } from 'preact';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { Footer } from '@/components/footer.tsx';
import { GoogleLoginButton } from '@/components/login/google-login-button.tsx';
import { KakaoLoginButton } from '@/components/login/kakao-login-button.tsx';
import { NaverLoginButton } from '@/components/login/naver-login-button.tsx';
import { finishLogin } from '@/lib/login-flow.ts';
import {
  ackOauthMessage,
  isOauthMessage,
  isOauthSuccess,
  isTrustedOauthOrigin,
  readReturnTo,
} from '@/lib/oauth.ts';
import { env } from '@/lib/env.ts';
import { formatPrice } from '@/lib/utils.ts';
import { INFO_TOTAL_POINT } from '@/data/policy.ts';
import { askAlert } from '@/store/dialog.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { DaoUser } from '@/types/dao.ts';
import type { PageProps } from '@/types/props.ts';

const Login: FunctionalComponent<PageProps> = () => {
  const dispatch = useAppDispatch();
  const isLogin = useAppSelector((state) => !!state.auth.user?.id);

  // 구 프로젝트는 복귀 주소를 state 에 담고 의존성 없는 useCallback 안에서 읽어
  // 항상 초기값('/')만 보였다. ref 로 들고 가 최신 값을 읽는다.
  const returnTo = useRef('/');

  const loginFail = useCallback(
    (message?: string) => {
      void dispatch(askAlert(message || '로그인이 실패하였습니다.'));
    },
    [dispatch]
  );

  const loginSuccess = useCallback(
    async (user: DaoUser) => {
      if (!user) return;

      // 세션 저장·유입 경로 정리·다음 화면 이동은 카카오 콜백 화면과 같이 쓴다(login-flow.ts).
      try {
        await finishLogin(dispatch, user, returnTo.current);
      } catch (err) {
        loginFail(err instanceof Error ? err.message : '로그인 정보를 저장하지 못했습니다.');
      }
    },
    [dispatch, loginFail]
  );

  const messageHandler = useCallback(
    (event: MessageEvent) => {
      if (!isOauthMessage(event.data)) return;

      if (!isTrustedOauthOrigin(event.origin)) {
        // 조용히 버리면 원인을 못 찾는다. 개발 중에는 남긴다.
        if (import.meta.env.DEV) {
          console.warn(
            `[oauth] 신뢰하지 않는 origin 의 메시지를 무시했습니다: ${event.origin}\n` +
              `허용: ${window.location.origin}, ${env.apiBase}`
          );
        }
        return;
      }

      // 팝업은 이 응답을 받아야 창을 닫는다. 못 받으면 팝업이 원인을 표시한다.
      ackOauthMessage(event);

      if (isOauthSuccess(event.data)) {
        void loginSuccess(event.data.payload);
      } else {
        loginFail(event.data.payload);
      }
    },
    [loginSuccess, loginFail]
  );

  useEffect(() => {
    returnTo.current = readReturnTo();
  }, []);

  /*
    ★ **다른 곳에서 로그인하다 실패하고 돌아온 경우** 이유를 보여준다. (2026-09-17)

      인앱 브라우저의 카카오 로그인은 페이지 전체가 카카오 -> 백엔드 -> 사이트로 도는데,
      중간에 실패하면 막다른 화면 대신 여기로 `?oauth_error=` 를 들려 돌려보낸다.

    ★ 보여준 뒤 주소에서 지운다. 남겨 두면 새로고침할 때마다 같은 알림이 뜬다.
  */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('oauth_error');
    if (!error) return;

    params.delete('oauth_error');
    const rest = params.toString();
    window.history.replaceState(null, '', `/login${rest ? `?${rest}` : ''}`);

    loginFail(error);
  }, [loginFail]);

  useEffect(() => {
    if (isLogin) route(readReturnTo(), true);
  }, [isLogin]);

  useEffect(() => {
    window.addEventListener('message', messageHandler);
    return () => window.removeEventListener('message', messageHandler);
  }, [messageHandler]);

  return (
    <div class="page page--login">
      <main class="login-main">
        <div class="login-headline">
          <h2>
            <a href="/">
              <img src="/images/logo_b.png" alt="urnplay" />
            </a>
          </h2>
          <h1 class="login-title">
            3초만에 로그인하고
            <br />
            포인트 벌기
          </h1>
          <ul class="login-benefits">
            <li>
              신규 회원가입 후 추가 정보 입력 시{' '}
              <span class="point-text">{formatPrice(INFO_TOTAL_POINT)}P</span> 지급
            </li>
          </ul>
        </div>

        <div class="login-buttons">
          <KakaoLoginButton onFail={loginFail} />
          <NaverLoginButton onFail={loginFail} />
          <GoogleLoginButton onComplete={loginSuccess} onFail={loginFail} />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Login;
